import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, User, Phone, Mail, Briefcase } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const EMPTY_FORM = {
  employeeid: null, companyid: null, employeecode: '', employeename: '',
  dateofbirth: '', gender: '', maritalstatus: '', bloodgroup: '',
  nationalid: '', passportnumber: '', passportexpiry: '',
  email: '', phone: '', mobile: '',
  addressline1: '', addressline2: '', cityid: '', stateid: '', postalcode: '', countryid: '', nationalityid: '',
  departmentid: '', designationid: '', employeetypeid: '',
  joiningdate: '', leavingdate: '', employmentstatusid: '', reportingto: '',
  basicsalary: '', accountnumber: '', iban: '', isactive: true,
};

const Employees = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [employees,         setEmployees]         = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [showModal,         setShowModal]         = useState(false);
  const [editMode,          setEditMode]          = useState(false);
  const [submitLoading,     setSubmitLoading]     = useState(false);
  const [errors,            setErrors]            = useState({});

  const [cities,             setCities]             = useState([]);
  const [states,             setStates]             = useState([]);
  const [countries,          setCountries]          = useState([]);
  const [departments,        setDepartments]        = useState([]);
  const [designations,       setDesignations]       = useState([]);
  const [employeeTypes,      setEmployeeTypes]      = useState([]);
  const [employmentStatuses, setEmploymentStatuses] = useState([]);
  const [employeesList,      setEmployeesList]      = useState([]);

  const [formData, setFormData] = useState({ ...EMPTY_FORM, companyid: companyId || null });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchDropdownData(); fetchEmployees();
  }, [companyId, yearId]);

  useEffect(() => { filterEmployees(); }, [searchTerm, employees]);

  const fetchDropdownData = async () => {
    try {
      const [cR, sR, coR, dR, dsR, etR, esR] = await Promise.all([
        apiClient.get('/Cities.php',             { params: { isactive: 1 } }),
        apiClient.get('/States.php',             { params: { isactive: 1 } }),
        apiClient.get('/Countries.php',          { params: { isactive: 1 } }),
        apiClient.get('/Departments.php'),
        apiClient.get('/Designations.php'),
        apiClient.get('/EmployeeTypes.php'),
        apiClient.get('/EmploymentStatuses.php'),
      ]);
      if (cR.data.success)  setCities(cR.data.data || []);
      if (sR.data.success)  setStates(sR.data.data || []);
      if (coR.data.success) setCountries(coR.data.data || []);
      if (dR.data.success)  setDepartments(dR.data.data || []);
      if (dsR.data.success) setDesignations(dsR.data.data || []);
      if (etR.data.success) setEmployeeTypes(etR.data.data || []);
      if (esR.data.success) setEmploymentStatuses(esR.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Employees.php', { params: { companyid: companyId } });
      if (res.data.success) { setEmployees(res.data.data || []); setEmployeesList(res.data.data || []); }
      else alert('Failed to fetch employees: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching employees. Please check if backend is running.');
    } finally { setLoading(false); }
  };

  const filterEmployees = () => {
    if (!searchTerm.trim()) { setFilteredEmployees(employees); return; }
    const q = searchTerm.toLowerCase();
    setFilteredEmployees(employees.filter(e =>
      e.employeename.toLowerCase().includes(q) || e.employeecode?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) || e.phone?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.employeename.trim()) errs.employeename = 'Employee name is required';
    if (!formData.employeetypeid) errs.employeetypeid = 'Employee type is required';
    if (!formData.employmentstatusid) errs.employmentstatusid = 'Employment status is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email format';
    if (formData.mobile && formData.mobile.length < 7) errs.mobile = 'Mobile number must be at least 7 digits';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const nullify = (v) => v || null;
      const payload = {
        ...formData, companyid: companyId, isactive: formData.isactive ? 1 : 0,
        cityid: nullify(formData.cityid), stateid: nullify(formData.stateid),
        countryid: nullify(formData.countryid), nationalityid: nullify(formData.nationalityid),
        departmentid: nullify(formData.departmentid), designationid: nullify(formData.designationid),
        employeetypeid: nullify(formData.employeetypeid), employmentstatusid: nullify(formData.employmentstatusid),
        reportingto: nullify(formData.reportingto), dateofbirth: nullify(formData.dateofbirth),
        gender: nullify(formData.gender), maritalstatus: nullify(formData.maritalstatus),
        bloodgroup: nullify(formData.bloodgroup), nationalid: nullify(formData.nationalid),
        passportnumber: nullify(formData.passportnumber), passportexpiry: nullify(formData.passportexpiry),
        email: nullify(formData.email), phone: nullify(formData.phone), mobile: nullify(formData.mobile),
        addressline1: nullify(formData.addressline1), addressline2: nullify(formData.addressline2),
        postalcode: nullify(formData.postalcode), joiningdate: nullify(formData.joiningdate),
        leavingdate: nullify(formData.leavingdate), basicsalary: nullify(formData.basicsalary),
        accountnumber: nullify(formData.accountnumber), iban: nullify(formData.iban),
      };
      const res = await apiClient[editMode ? 'put' : 'post']('/Employees.php', payload);
      if (res.data.success) {
        alert(!editMode && res.data.data?.employeecode
          ? `Employee created successfully!\nEmployee Code: ${res.data.data.employeecode}`
          : editMode ? 'Employee updated successfully!' : 'Employee created successfully!');
        closeModal(); fetchEmployees();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error: ' + (err.response?.data?.message || 'Error saving employee. Please try again.'));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (emp) => {
    setFormData({
      employeeid: emp.employeeid, companyid: emp.companyid, employeecode: emp.employeecode,
      employeename: emp.employeename, dateofbirth: emp.dateofbirth || '', gender: emp.gender || '',
      maritalstatus: emp.maritalstatus || '', bloodgroup: emp.bloodgroup || '',
      nationalid: emp.nationalid || '', passportnumber: emp.passportnumber || '',
      passportexpiry: emp.passportexpiry || '', email: emp.email || '',
      phone: emp.phone || '', mobile: emp.mobile || '',
      addressline1: emp.addressline1 || '', addressline2: emp.addressline2 || '',
      cityid: emp.cityid || '', stateid: emp.stateid || '', postalcode: emp.postalcode || '',
      countryid: emp.countryid || '', nationalityid: emp.nationalityid || '',
      departmentid: emp.departmentid || '', designationid: emp.designationid || '',
      employeetypeid: emp.employeetypeid || '', joiningdate: emp.joiningdate || '',
      leavingdate: emp.leavingdate || '', employmentstatusid: emp.employmentstatusid || '',
      reportingto: emp.reportingto || '', basicsalary: emp.basicsalary || '',
      accountnumber: emp.accountnumber || '', iban: emp.iban || '',
      isactive: emp.isactive === 1,
    });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      const res = await apiClient.delete('/Employees.php', { data: { employeeid: id } });
      if (res.data.success) { alert('Employee deleted successfully!'); fetchEmployees(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting employee. Please try again.');
    }
  };

  const set = (field, value) => setFormData(p => ({ ...p, [field]: value }));
  const resetForm    = () => { setFormData({ ...EMPTY_FORM, companyid: companyId || null }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  // shared styles
  const inputCls  = (err) => `w-full px-3 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const labelCls  = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-[11px] text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const SectionLabel = ({ title }) => (
    <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
      px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
      style={{ borderLeftColor: NAVY }}>{title}</div>
  );

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <User size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <User size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Employees Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage employee information</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Employee
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by name, code, email, or phone..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredEmployees.length} {filteredEmployees.length === 1 ? 'employee' : 'employees'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading employees...</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <User size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No employees found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Code', 'Employee Name', 'Department', 'Designation', 'Contact', 'Joining Date', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(emp => (
                  <tr key={emp.employeeid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {emp.employeecode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{emp.employeename}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{emp.departmentname || '-'}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{emp.designationname || '-'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        {emp.mobile && <div className="flex items-center gap-1.5 text-sm text-gray-600"><Phone size={12} className="text-gray-400 flex-shrink-0"/>{emp.mobile}</div>}
                        {emp.email  && <div className="flex items-center gap-1.5 text-sm text-gray-600"><Mail size={12} className="text-gray-400 flex-shrink-0"/>{emp.email}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {emp.joiningdate ? new Date(emp.joiningdate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        emp.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {emp.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(emp)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(emp.employeeid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[860px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <User size={18} /> {editMode ? 'Edit Employee' : 'Add New Employee'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* ── 1. BASIC ── */}
              <SectionLabel title="Basic Information" />
              {editMode && formData.employeecode && (
                <div className="mb-4">
                  <label className={labelCls}>Employee Code (Auto-generated)</label>
                  <input type="text" value={formData.employeecode} disabled
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-100 cursor-not-allowed" />
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Employee Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.employeename} onChange={e => set('employeename', e.target.value)}
                    maxLength="100" placeholder="Full Name" className={inputCls(errors.employeename)} />
                  {errMsg(errors.employeename)}
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input type="date" value={formData.dateofbirth} onChange={e => set('dateofbirth', e.target.value)} className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Gender</label>
                  <select value={formData.gender} onChange={e => set('gender', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select --</option>
                    {['Male','Female','Other'].map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Marital Status</label>
                  <select value={formData.maritalstatus} onChange={e => set('maritalstatus', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select --</option>
                    {['Single','Married','Divorced','Widowed'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Blood Group</label>
                  <select value={formData.bloodgroup} onChange={e => set('bloodgroup', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select --</option>
                    {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              {/* ── 2. IDENTITY ── */}
              <SectionLabel title="Identity Documents" />
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div>
                  <label className={labelCls}>National ID</label>
                  <input type="text" value={formData.nationalid} onChange={e => set('nationalid', e.target.value)} maxLength="50" placeholder="National ID Number" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Passport Number</label>
                  <input type="text" value={formData.passportnumber} onChange={e => set('passportnumber', e.target.value)} maxLength="50" placeholder="Passport Number" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Passport Expiry</label>
                  <input type="date" value={formData.passportexpiry} onChange={e => set('passportexpiry', e.target.value)} className={inputCls(false)} />
                </div>
              </div>

              {/* ── 3. CONTACT ── */}
              <SectionLabel title="Contact Information" />
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={formData.email} onChange={e => set('email', e.target.value)} maxLength="100" placeholder="email@example.com" className={inputCls(errors.email)} />
                  {errMsg(errors.email)}
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="tel" value={formData.phone} onChange={e => set('phone', e.target.value)} maxLength="20" placeholder="Phone Number" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Mobile</label>
                  <input type="tel" value={formData.mobile} onChange={e => set('mobile', e.target.value)} maxLength="20" placeholder="Mobile Number" className={inputCls(errors.mobile)} />
                  {errMsg(errors.mobile)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Address Line 1</label>
                  <input type="text" value={formData.addressline1} onChange={e => set('addressline1', e.target.value)} maxLength="255" placeholder="Street, Building" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Address Line 2</label>
                  <input type="text" value={formData.addressline2} onChange={e => set('addressline2', e.target.value)} maxLength="255" placeholder="Area, Landmark" className={inputCls(false)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>City</label>
                  <select value={formData.cityid} onChange={e => set('cityid', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select City --</option>
                    {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Postal Code</label>
                  <input type="text" value={formData.postalcode} onChange={e => set('postalcode', e.target.value)} maxLength="20" placeholder="Postal/ZIP Code" className={inputCls(false)} />
                </div>
              </div>

              {/* ── 4. EMPLOYMENT ── */}
              <SectionLabel title="Employment Details" />
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Employee Type <span className="text-red-500">*</span></label>
                  <select value={formData.employeetypeid} onChange={e => set('employeetypeid', e.target.value)} className={selectCls(errors.employeetypeid)} required>
                    <option value="">-- Select Employee Type --</option>
                    {employeeTypes.map(t => <option key={t.employeetypeid} value={t.employeetypeid}>{t.typename}</option>)}
                  </select>
                  {errMsg(errors.employeetypeid)}
                </div>
                <div>
                  <label className={labelCls}>Employment Status <span className="text-red-500">*</span></label>
                  <select value={formData.employmentstatusid} onChange={e => set('employmentstatusid', e.target.value)} className={selectCls(errors.employmentstatusid)} required>
                    <option value="">-- Select Status --</option>
                    {employmentStatuses.map(s => <option key={s.statusid} value={s.statusid}>{s.statusname}</option>)}
                  </select>
                  {errMsg(errors.employmentstatusid)}
                </div>
                <div>
                  <label className={labelCls}>Joining Date</label>
                  <input type="date" value={formData.joiningdate} onChange={e => set('joiningdate', e.target.value)} className={inputCls(false)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Department</label>
                  <select value={formData.departmentid} onChange={e => set('departmentid', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select Department --</option>
                    {departments.map(d => <option key={d.departmentid} value={d.departmentid}>{d.departmentname}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Designation</label>
                  <select value={formData.designationid} onChange={e => set('designationid', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select Designation --</option>
                    {designations.map(d => <option key={d.designationid} value={d.designationid}>{d.designationname}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Reporting To</label>
                  <select value={formData.reportingto} onChange={e => set('reportingto', e.target.value)} className={selectCls(false)}>
                    <option value="">-- Select Manager --</option>
                    {employeesList.filter(e => e.employeeid !== formData.employeeid).map(e => (
                      <option key={e.employeeid} value={e.employeeid}>{e.employeename} ({e.employeecode})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Leaving Date</label>
                  <input type="date" value={formData.leavingdate} onChange={e => set('leavingdate', e.target.value)} className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>Basic Salary</label>
                  <input type="number" value={formData.basicsalary} onChange={e => set('basicsalary', e.target.value)} min="0" step="0.01" placeholder="0.00" className={inputCls(false) + ' text-right'} />
                </div>
              </div>

              {/* ── 5. BANKING ── */}
              <SectionLabel title="Banking Information" />
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Account Number</label>
                  <input type="text" value={formData.accountnumber} onChange={e => set('accountnumber', e.target.value)} maxLength="50" placeholder="Bank Account Number" className={inputCls(false)} />
                </div>
                <div>
                  <label className={labelCls}>IBAN</label>
                  <input type="text" value={formData.iban} onChange={e => set('iban', e.target.value)} maxLength="50" placeholder="International Bank Account Number" className={inputCls(false)} />
                </div>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive} onChange={e => set('isactive', e.target.checked)}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-6 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Employees;